import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
  useMemo,
} from "react";

interface ProfileCommission {
  data: any;
}

interface ProfileCommissionContextType {
  loading: boolean;
  commission: any;
  error: string | null;
  refreshCommission: () => Promise<void>;
  setCommission?: any;
}

interface Props {
  children: ReactNode;
}

const ProfileCommissionContext = createContext<ProfileCommissionContextType | undefined>(undefined);

const apiUrl = process.env.REACT_APP_API_URL;

export const ProfileCommissionProvider = ({ children }: Props) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const [commission, setCommission] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCommission = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();

      const response = await axios.get<any>(
        `${apiUrl}/api/v1/admin/profile/commission`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }

    );
      setCommission(response.data.response);
    } catch (err: any) {
      setError(err.message || "Error al obtener el total de comisión");
      setCommission(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    fetchCommission();
  }, [fetchCommission]);

  const commissionFormatted = useMemo(() => {
    if (!commission) return null;
    return {
      totalTransactions: commission[0]?.total_transaccion || 0,
      totalCost: commission[0]?.costo_total_sin_comision || 0,
    };
  }, [commission]);

  return (
    <ProfileCommissionContext.Provider
      value={{
        commission: commissionFormatted ?? commission,
        loading,
        error,
        refreshCommission: fetchCommission,
        setCommission,
      }}
    >
      {children}
    </ProfileCommissionContext.Provider>
  );
};

export const useProfileCommission = () => {
  const context = useContext(ProfileCommissionContext);
  if (!context) {
    throw new Error(
      "useProfileCommission debe usarse dentro de un ProfileCommissionProvider"
    );
  }
  return context;
};
